import { CustomAttributes } from "../../../../pojo/CustomAttributes";
import { MasterDefinitionService } from "../../../../services/master.definitions/master.definition.service";
import { S25Util } from "../../../../util/s25-util";
import { RowData } from "../../../s25-spreadsheet/types/sheet.js.types.expanded";
import { MasterDefinitionImportItem, MasterDefinitionImportKind } from "../../import.service";
import { ImportUtil } from "../import.util";

export { MasterDefinitionImportKind };

export const CUSTOM_ATTRIBUTE_IMPORT_TYPES: ReadonlyArray<{ code: string; label: string }> = [
    { code: "S", label: "Text" },
    { code: "X", label: "Long Text" },
    { code: "R", label: "URL/File" },
    { code: "N", label: "Integer" },
    { code: "F", label: "Float" },
    { code: "B", label: "Boolean" },
    { code: "D", label: "Date" },
    { code: "E", label: "Datetime" },
    { code: "T", label: "Time" },
    { code: "I", label: "Image" },
    { code: "2", label: "Organization" },
    { code: "3", label: "Contact" },
    { code: "4", label: "Location" },
    { code: "6", label: "Resource" },
    { code: "L", label: "Duration" },
];

type MappedField =
    | "id"
    | "name"
    | "active"
    | "dataType"
    | "multiVal"
    | "allowComment"
    | "allowQty"
    | "schedule25"
    | "rateGroup"
    | "organizationCode"
    | "nodeType"
    | "parent";

const FIELD_ALIASES: ReadonlyArray<readonly [MappedField, readonly string[]]> = [
    ["id", ["id", "item_id"]],
    ["name", ["name"]],
    ["active", ["active"]],
    ["nodeType", ["node_type", "node type"]],
    ["parent", ["parent", "parent_name", "parent_id"]],
    ["dataType", ["type", "data_type", "datatype"]],
    ["multiVal", ["multi_value", "multival", "multi_val"]],
    ["allowComment", ["allow_comment", "req_comment"]],
    ["allowQty", ["allow_quantity", "allow_qty", "stock_count"]],
    ["schedule25", ["schedule25", "s25", "s25_feature"]],
    ["rateGroup", ["rate_group", "rategroup"]],
    ["organizationCode", ["organization_code", "org_code", "acct_code"]],
];

function catalog(kind: MasterDefinitionImportKind) {
    if (kind === MasterDefinitionImportKind.RATE_GROUP) {
        return undefined;
    }
    return MasterDefinitionService.masterDefsConst[
        kind.toLowerCase() as keyof typeof MasterDefinitionService.masterDefsConst
    ];
}

export class MasterDefinitionSpreadsheetUtil {
    static isCustomAttributeKind(kind: MasterDefinitionImportKind): boolean {
        return kind !== MasterDefinitionImportKind.RATE_GROUP && !!catalog(kind)?.hasType;
    }

    static requiredColumns(kind: MasterDefinitionImportKind): string[] {
        if (kind === MasterDefinitionImportKind.EVENT_TYPE) {
            return ["Name", "Node Type", "Parent"];
        }
        if (this.isCustomAttributeKind(kind)) {
            return ["Name", "Type"];
        }
        return ["Name"];
    }

    static nameMaxLength(kind: MasterDefinitionImportKind): number {
        if (kind === MasterDefinitionImportKind.RATE_GROUP) {
            return 40;
        }
        return catalog(kind)!.nameLen;
    }

    static rowsToItems(
        kind: MasterDefinitionImportKind,
        rows: RowData[],
        errorSet: Set<string>,
    ): MasterDefinitionImportItem[] {
        if (!rows?.length) {
            return [];
        }
        const missing = ImportUtil.getMissingRequiredColumns(headerMap(rows[0]), this.requiredColumns(kind));
        if (missing.length) {
            errorSet.add(`Missing required column(s): ${missing.join(", ")}`);
            return [];
        }

        const fieldByCol = mapHeaderFields(rows[0]);
        const items: MasterDefinitionImportItem[] = [];
        for (let rowIndex = 1; rowIndex < rows.length; rowIndex++) {
            const item = this.rowToItem(rows[rowIndex], fieldByCol, errorSet, rowIndex + 1);
            if (item) {
                items.push(item);
            }
        }
        this.validateItems(kind, items, errorSet);
        return items;
    }

    static validateItems(
        kind: MasterDefinitionImportKind,
        items: MasterDefinitionImportItem[],
        errorSet: Set<string>,
    ): void {
        const nameMax = this.nameMaxLength(kind);
        const seenNames = new Set<string>();
        const seenIds = new Set<number>();
        for (const item of items) {
            if (!item.name?.trim()) {
                errorSet.add("Name is required");
                continue;
            }
            if (item.name.length > nameMax) {
                errorSet.add(`Name cannot exceed ${nameMax} characters: "${item.name.substring(0, nameMax)}..."`);
            }
            const trimmedName = item.name.trim();
            const nameKey = trimmedName.toLowerCase();
            if (seenNames.has(nameKey)) {
                errorSet.add(`Duplicate name "${trimmedName}"`);
            } else {
                seenNames.add(nameKey);
            }
            if (item.id != null) {
                if (seenIds.has(item.id)) {
                    errorSet.add(`Duplicate ID ${item.id}`);
                } else {
                    seenIds.add(item.id);
                }
            }
            if (kind === MasterDefinitionImportKind.EVENT_TYPE) {
                this.validateEventTypeItem(item, items, errorSet);
            }
        }
    }

    static templateRow(kind: MasterDefinitionImportKind): Record<string, string> {
        const row: Record<string, string> = { Name: "Example Name", ID: "", Active: "true" };
        if (this.isCustomAttributeKind(kind)) {
            row.Type = "S";
            if (kind === MasterDefinitionImportKind.EVENT_CUSTOM_ATTRIBUTE) {
                row["Multi Value"] = "false";
            }
        }
        if (kind === MasterDefinitionImportKind.OTHER_REQUIREMENT) {
            row["Allow Comment"] = "true";
            row["Allow Quantity"] = "true";
        }
        if (kind === MasterDefinitionImportKind.CALENDAR_REQUIREMENT) {
            row["Allow Comment"] = "true";
        }
        if (kind === MasterDefinitionImportKind.FEATURE) {
            row.Schedule25 = "false";
        }
        if (kind === MasterDefinitionImportKind.ORG_TYPE) {
            row["Rate Group"] = "";
        }
        if (kind === MasterDefinitionImportKind.RATE_GROUP) {
            row["Organization Code"] = "";
        }
        if (kind === MasterDefinitionImportKind.EVENT_TYPE) {
            row["Node Type"] = "E";
            row.Parent = "Example Folder Type";
        }
        return row;
    }

    private static rowToItem(
        row: RowData,
        fieldByCol: Map<number, MappedField>,
        errorSet: Set<string>,
        rowNumber: number,
    ): MasterDefinitionImportItem | undefined {
        const item: MasterDefinitionImportItem = {};
        const cells = row?.cells ?? [];
        for (const [col, field] of fieldByCol) {
            const raw = cells[col]?.text;
            if (raw == null || String(raw).trim() === "") {
                continue;
            }
            this.applyField(item, field, String(raw).trim(), errorSet, rowNumber);
        }
        if (!item.name && item.id == null) {
            return undefined;
        }
        return item;
    }

    private static applyField(
        item: MasterDefinitionImportItem,
        field: MappedField,
        value: string,
        errorSet: Set<string>,
        rowNumber: number,
    ): void {
        switch (field) {
            case "id": {
                const parsed = ImportUtil.parseIdOrName(value);
                if (parsed.id != null) {
                    item.id = parsed.id;
                } else if (parsed.name) {
                    errorSet.add(`Row ${rowNumber}: ID must be numeric`);
                }
                break;
            }
            case "name": {
                const parsed = ImportUtil.parseIdOrName(value);
                item.name = parsed.name ?? (parsed.id != null ? String(parsed.id) : undefined);
                break;
            }
            case "active": {
                const flag = S25Util.bool.parseBoolean(value);
                if (typeof flag !== "boolean") {
                    errorSet.add(`Row ${rowNumber}: Active must be 1/0/true/false`);
                } else {
                    item.active = flag;
                }
                break;
            }
            case "dataType": {
                const code = value.trim();
                if (!CustomAttributes.typeCodes.has(code)) {
                    errorSet.add(`Row ${rowNumber}: unknown Type "${code}"`);
                } else {
                    item.dataType = code;
                }
                break;
            }
            case "multiVal": {
                const flag = S25Util.bool.parseBoolean(value);
                if (typeof flag !== "boolean") {
                    errorSet.add(`Row ${rowNumber}: Multi Value must be 1/0/true/false`);
                } else {
                    item.multiVal = flag;
                }
                break;
            }
            case "allowComment": {
                const flag = S25Util.bool.parseBoolean(value);
                if (typeof flag !== "boolean") {
                    errorSet.add(`Row ${rowNumber}: Allow Comment must be 1/0/true/false`);
                } else {
                    item.allowComment = flag;
                }
                break;
            }
            case "allowQty": {
                const flag = S25Util.bool.parseBoolean(value);
                if (typeof flag !== "boolean") {
                    errorSet.add(`Row ${rowNumber}: Allow Quantity must be 1/0/true/false`);
                } else {
                    item.allowQty = flag;
                }
                break;
            }
            case "schedule25": {
                const flag = S25Util.bool.parseBoolean(value);
                if (typeof flag !== "boolean") {
                    errorSet.add(`Row ${rowNumber}: Schedule25 must be 1/0/true/false`);
                } else {
                    item.schedule25 = flag;
                }
                break;
            }
            case "rateGroup": {
                const parsed = ImportUtil.parseIdOrName(value);
                if (parsed.id != null) {
                    item.rateGroupId = parsed.id;
                } else if (parsed.name) {
                    item.rateGroupName = parsed.name;
                }
                break;
            }
            case "organizationCode":
                item.organizationCode = value;
                break;
            case "nodeType": {
                const parsed = parseNodeType(value);
                if (parsed === undefined) {
                    errorSet.add(`Row ${rowNumber}: node type must be C (cabinet), F (folder), or E (event type)`);
                } else {
                    item.nodeType = parsed;
                }
                break;
            }
            case "parent": {
                const parsed = ImportUtil.parseIdOrName(value);
                if (parsed.id != null) {
                    item.parentId = parsed.id;
                } else if (parsed.name) {
                    item.parentName = parsed.name;
                }
                break;
            }
            default:
                break;
        }
    }

    private static validateEventTypeItem(
        item: MasterDefinitionImportItem,
        items: MasterDefinitionImportItem[],
        errorSet: Set<string>,
    ): void {
        if (!item.nodeType) {
            errorSet.add("Node type is required");
            return;
        }
        const hasParent = (item.parentId != null && item.parentId !== 0) || !!item.parentName?.trim();
        if (item.nodeType === "C" && hasParent) {
            errorSet.add("Cabinet types cannot have a parent.");
            return;
        }
        if (item.nodeType === "F" && !hasParent) {
            errorSet.add("Parent is required for a folder");
            return;
        }
        if (item.nodeType === "E" && !hasParent) {
            errorSet.add("Parent is required for an event type");
            return;
        }
        const parentItem = findSheetParent(item, items);
        if (!parentItem?.nodeType) {
            return;
        }
        if (item.nodeType === "F" && parentItem.nodeType !== "C") {
            errorSet.add("A folder must parent to a cabinet");
        }
        if (item.nodeType === "E" && parentItem.nodeType === "C") {
            errorSet.add("An event type cannot sit directly under a cabinet; parent must be a folder");
        } else if (item.nodeType === "E" && parentItem.nodeType !== "F") {
            errorSet.add("An event type must parent to a folder");
        }
    }
}

function headerMap(headerRow: RowData): Map<number, string> {
    const headers = new Map<number, string>();
    headerRow?.cells?.forEach((cell, col) => {
        if (cell?.text) {
            headers.set(col, String(cell.text));
        }
    });
    return headers;
}

function mapHeaderFields(headerRow: RowData): Map<number, MappedField> {
    const fieldByCol = new Map<number, MappedField>();
    headerRow?.cells?.forEach((cell, col) => {
        if (!cell?.text) {
            return;
        }
        const normalized = ImportUtil.normalize(String(cell.text));
        for (const [field, aliases] of FIELD_ALIASES) {
            if (aliases.some((alias) => ImportUtil.normalize(alias) === normalized)) {
                fieldByCol.set(col, field);
                return;
            }
        }
    });
    return fieldByCol;
}

function findSheetParent(
    item: MasterDefinitionImportItem,
    items: MasterDefinitionImportItem[],
): MasterDefinitionImportItem | undefined {
    if (item.parentId != null && item.parentId !== 0) {
        return items.find((other) => other.id === item.parentId);
    }
    const parentName = item.parentName?.trim().toLowerCase();
    if (!parentName) {
        return undefined;
    }
    return items.find((other) => other.name?.trim().toLowerCase() === parentName);
}

function parseNodeType(value: string): string | undefined {
    const lowered = value.trim().toLowerCase();
    if (lowered === "c" || lowered === "cabinet") {
        return "C";
    }
    if (lowered === "f" || lowered === "folder") {
        return "F";
    }
    if (lowered === "e" || lowered === "event" || lowered === "event type" || lowered === "event_type") {
        return "E";
    }
    return undefined;
}
